import axios from "axios";
import { createIdempotencyKey } from "./baasConsoleClient";

/**
 * Public KYC invite APIs on BaaS (no partner key / no console JWT).
 * Base: REACT_APP_BAAS_PUBLIC_URL (e.g. https://api.valut.services/baas or local Baas).
 *
 * BaaS IdempotencyFilter requires X-Idempotency-Key on every POST.
 */

const baasPublicBaseUrl = () => {
  // CRA inlines REACT_APP_* — avoid `typeof process` (short-circuits in browser).
  const fromEnv = (process.env.REACT_APP_BAAS_PUBLIC_URL || "").trim();
  const fallback =
    process.env.NODE_ENV === "development" ? "http://localhost:5188" : "";
  return (fromEnv || fallback).replace(/\/+$/, "");
};

const ensureIdempotencyKey = (config) => {
  const method = String(config.method || "get").toUpperCase();
  if (method !== "POST") {
    return config;
  }

  const headers = config.headers || {};
  const existing =
    headers["X-Idempotency-Key"] ?? headers["x-idempotency-key"];
  if (!existing) {
    headers["X-Idempotency-Key"] = createIdempotencyKey();
    config.headers = headers;
  }
  return config;
};

const publicClient = () => {
  const base = baasPublicBaseUrl();
  if (!base) {
    throw new Error("REACT_APP_BAAS_PUBLIC_URL is not configured");
  }
  const client = axios.create({
    baseURL: base,
    timeout: 30000,
  });
  client.interceptors.request.use(ensureIdempotencyKey);
  return client;
};

/** True when the browser never got an HTTP response (CORS block, offline, DNS, refused). */
export const isBaasKycNetworkError = (error) => {
  if (!error) return false;
  if (error.isNetworkError === true) return true;
  if (error.status != null) return false;
  const msg = String(error.message || "").toLowerCase();
  return (
    msg === "network error"
    || msg.includes("failed to fetch")
    || error.code === "ERR_NETWORK"
    || error.code === "ECONNABORTED"
  );
};

const unwrapError = (error) => {
  const data = error?.response?.data;
  const status = error?.response?.status ?? null;
  const isNetwork =
    status == null
    && (
      error?.code === "ERR_NETWORK"
      || error?.code === "ECONNABORTED"
      || String(error?.message || "").toLowerCase() === "network error"
      || String(error?.message || "").toLowerCase().includes("failed to fetch")
    );
  const normalized = new Error(
    data?.detail || data?.title || data?.message || error?.message || "KYC invite request failed",
  );
  normalized.name = "BaasKycInviteError";
  normalized.status = status;
  normalized.code = data?.error || data?.code || error?.code || null;
  normalized.detail = data?.detail || null;
  normalized.data = data;
  normalized.isNetworkError = isNetwork;
  return normalized;
};

export const fetchPublicKycInviteStatus = async (token) => {
  try {
    const { data } = await publicClient().get(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}`,
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const sendPublicKycInviteEmailOtp = async (token, email) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/email/send`,
      { email },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const verifyPublicKycInviteEmailOtp = async (token, code) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/email/verify`,
      { code },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const sendPublicKycInvitePhoneOtp = async (token, phoneNumber) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/phone/send`,
      { phone_number: phoneNumber },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const verifyPublicKycInvitePhoneOtp = async (token, code) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/phone/verify`,
      { code },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const startPublicKycInviteVeriff = async (token) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/veriff/session`,
      {},
      {
        headers: {
          "X-Idempotency-Key": `kyc-invite-veriff:${String(token || "").trim()}`,
        },
      },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

export const reconcilePublicKycInviteVeriff = async (token, sessionId) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/veriff/reconcile`,
      { session_id: sessionId || undefined },
      {
        headers: {
          "X-Idempotency-Key": `kyc-invite-reconcile:${String(token || "").trim()}:${String(sessionId || "").trim()}`,
        },
      },
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};

/**
 * Blank onboard: submit PF profile after email+phone OTP → creates PERSON, then Veriff.
 * BaaS `POST /v1/public/kyc-invite/{token}/profile`
 */
export const submitPublicKycInviteProfile = async (token, profile) => {
  try {
    const { data } = await publicClient().post(
      `/v1/public/kyc-invite/${encodeURIComponent(token)}/profile`,
      profile,
    );
    return data;
  } catch (error) {
    throw unwrapError(error);
  }
};
